/**
 * Cross-platform campaign info form (#4288 Phase 3b).
 *
 * Presentational sibling of Google's :file:`components/campaign/settings/campaign-info-view.tsx`
 * and Meta's pre-#4288 inline ``MetaCampaignOverview`` rows. Both surfaces were rendering the same
 * "label + inline-styled disabled input" pattern with platform-shaped data. This component owns
 * the visual shape; each platform's adapter just maps its data → :class:`CampaignInfoField`.
 *
 * For now every row is read-only. ``editable`` is wired through the field interface for the
 * follow-up that lets Meta campaign attributes (name, target CPL) become editable post-create.
 */

import { Input } from '@/components/ui/input';

/** One row of the info form. Read-only by default; ``onChange`` makes it editable. */
export interface CampaignInfoField {
  label: string;
  value: string;
  /** Render the input in monospace (for IDs and other identifier-shaped values). */
  monospace?: boolean;
  /** Set when this field is editable; absence ⇒ read-only. */
  onChange?: (next: string) => void;
  /** Max length for editable fields (passes through to the underlying input). */
  maxLength?: number;
  /** Placeholder for editable fields. */
  placeholder?: string;
}

/** A form entry: one full-width field, or an array rendered as one grid row (Google's layout). */
export type CampaignInfoRow = CampaignInfoField | CampaignInfoField[];

interface CampaignInfoFormProps {
  fields: CampaignInfoRow[];
}

const READ_ONLY_INPUT_CLASS =
  '!ring-inset !ring-transparent text-gray-900 disabled:opacity-100 disabled:text-gray-900';
const INPUT_INHERIT_STYLE = { fontSize: 'inherit', lineHeight: 'inherit', fontFamily: 'inherit' } as const;

export function CampaignInfoForm({ fields }: CampaignInfoFormProps) {
  // ``space-y-6`` matches the Google ``CampaignInfoView`` rhythm. An array entry renders its
  // fields side-by-side on one grid row — Google's Overview puts Website URL | Campaign Type |
  // Created On on a single line, and Meta mirrors that (#5981). Consumers wrap the form to
  // compose with other rows — the form doesn't pad itself.
  return (
    <div className="space-y-6">
      {fields.map((row) =>
        Array.isArray(row) ? (
          <div
            key={row.map((field) => field.label).join('|')}
            className="grid gap-6"
            style={{ gridTemplateColumns: `repeat(${row.length}, minmax(0, 1fr))` }}
          >
            {row.map((field) => (
              <InfoField key={field.label} field={field} />
            ))}
          </div>
        ) : (
          <InfoField key={row.label} field={row} />
        ),
      )}
    </div>
  );
}

function InfoField({ field }: { field: CampaignInfoField }) {
  return (
    <div>
      <h3 className="text-slate-800 font-semibold mb-2">{field.label}</h3>
      <Input
        value={field.value}
        onChange={field.onChange ? (e) => field.onChange?.(e.target.value) : undefined}
        maxLength={field.maxLength}
        placeholder={field.placeholder}
        className={`${READ_ONLY_INPUT_CLASS}${field.monospace ? ' font-mono text-xs' : ''}`}
        style={INPUT_INHERIT_STYLE}
        disabled={!field.onChange}
        readOnly={!field.onChange}
      />
    </div>
  );
}
